import {
  Chart,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Legend,
  Tooltip,
  type ChartConfiguration,
} from "chart.js";
import type {
  AlertaGap,
  Documento,
  Holerite,
  SyncResponse,
} from "@qz/shared";
import { apiDownload, ApiError } from "../api.js";
import { clearSession, state } from "../state.js";
import { navigate } from "../main.js";

Chart.register(
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Legend,
  Tooltip,
);

const MES_LABELS = [
  "jan", "fev", "mar", "abr", "mai", "jun",
  "jul", "ago", "set", "out", "nov", "dez",
];

function fmtBR(v: number): string {
  return v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtRS(v: number): string {
  return "R$ " + fmtBR(v);
}
function competenciaLabel(comp: string): string {
  if (!comp.includes("/")) return comp;
  const [mm, yyyy] = comp.split("/");
  return `${MES_LABELS[parseInt(mm!, 10) - 1]}/${yyyy!.slice(-2)}`;
}

// ---------------- Tipos derivados ----------------

type DocComp = Documento & { empresa_cnpj: string; empresa_nome: string };

interface HoleriteView extends Holerite {
  comp_label: string;
  empresa_nome: string;
}

interface MesConsolidado {
  cnpj: string;
  comp_key: string;
  comp_label: string;
  bruto_mensal: number;
  decimo_terceiro: number;
  liquido_total: number;
  liquido_adi: number;
  liquido_mensal: number;
  liquido_13: number;
  inss: number;
  irrf: number;
  fgts: number;
  plano: number;
  refeicao: number;
  taxa: number;
  outros: number;
  has_mensal: boolean;
}

interface MesAgregado {
  key: string;
  label: string;
  bruto: number;
  liquido: number;
  inss: number;
  irrf: number;
  fgts: number;
  plano: number;
  refeicao: number;
  taxa: number;
  outros: number;
  has_mensal: boolean;
}

// ---------------- Estado dos filtros ----------------

let cnpjAtivo = "todas";
let anoAtivo = "2025";
let mesAtivo = "todos";

let chartsAtivos: Chart[] = [];
function clearCharts() {
  chartsAtivos.forEach((c) => {
    try { c.destroy(); } catch { /* ignore */ }
  });
  chartsAtivos = [];
}

// ---------------- Filtragem & consolidação ----------------

function holeritesEnriquecidos(data: SyncResponse): HoleriteView[] {
  return data.holerites.map((h) => ({
    ...h,
    comp_label: competenciaLabel(h.competencia),
    empresa_nome: data.empresas[h.cnpj] ?? h.cnpj,
  }));
}

function deduplicar(rows: HoleriteView[]): HoleriteView[] {
  const byKey = new Map<string, HoleriteView>();
  for (const r of rows) {
    const k = `${r.cnpj}|${r.comp_key}|${r.tipo_recibo}`;
    const cur = byKey.get(k);
    if (!cur || Number(r.key) > Number(cur.key)) byKey.set(k, r);
  }
  return Array.from(byKey.values());
}

function rowsFiltradosBruto(data: SyncResponse): HoleriteView[] {
  let rows = holeritesEnriquecidos(data);
  if (cnpjAtivo !== "todas") rows = rows.filter((r) => r.cnpj === cnpjAtivo);
  if (anoAtivo !== "todos") {
    if (mesAtivo !== "todos") {
      const alvo = `${anoAtivo}-${mesAtivo}`;
      rows = rows.filter((r) => r.comp_key === alvo);
    } else {
      rows = rows.filter((r) => r.comp_key.startsWith(anoAtivo));
    }
  }
  return rows;
}
function rowsFiltrados(data: SyncResponse): HoleriteView[] {
  return deduplicar(rowsFiltradosBruto(data));
}
function rowsReemissoes(
  data: SyncResponse,
): Array<HoleriteView & { _reemitido: boolean }> {
  const vencedoras = new Set(rowsFiltrados(data).map((r) => r.doc_id));
  return rowsFiltradosBruto(data).map((r) => ({
    ...r,
    _reemitido: !vencedoras.has(r.doc_id),
  }));
}

function rowsDoFiltroSemMes(data: SyncResponse): HoleriteView[] {
  let rows = holeritesEnriquecidos(data);
  if (cnpjAtivo !== "todas") rows = rows.filter((r) => r.cnpj === cnpjAtivo);
  return deduplicar(rows);
}

function consolidarPorMes(rows: HoleriteView[]): MesConsolidado[] {
  const map = new Map<string, MesConsolidado>();
  for (const r of rows) {
    const k = `${r.cnpj}|${r.comp_key}`;
    if (!map.has(k)) {
      map.set(k, {
        cnpj: r.cnpj,
        comp_key: r.comp_key,
        comp_label: r.comp_label,
        bruto_mensal: 0, decimo_terceiro: 0,
        liquido_total: 0, liquido_adi: 0, liquido_mensal: 0, liquido_13: 0,
        inss: 0, irrf: 0, fgts: 0,
        plano: 0, refeicao: 0, taxa: 0, outros: 0,
        has_mensal: false,
      });
    }
    const e = map.get(k)!;
    e.liquido_total += r.liquido;
    e.inss += r.inss_valor;
    e.irrf += r.irrf_valor;
    e.fgts += r.fgts_mes;
    e.plano += r.plano_saude;
    e.refeicao += r.desconto_refeicao;
    e.taxa += r.taxa_assistencial;
    e.outros += r.outros_descontos;
    if (r.tipo_recibo === "Mensal") {
      e.bruto_mensal = r.total_proventos;
      e.liquido_mensal += r.liquido;
      e.has_mensal = true;
    } else if (r.tipo_recibo.startsWith("Adiant")) {
      e.liquido_adi += r.liquido;
    } else if (r.tipo_recibo.startsWith("13")) {
      e.decimo_terceiro += r.total_proventos;
      e.liquido_13 += r.liquido;
    }
  }
  return Array.from(map.values()).sort((a, b) => a.comp_key.localeCompare(b.comp_key));
}

function agregarPorCompetencia(meses: MesConsolidado[]): MesAgregado[] {
  const map = new Map<string, MesAgregado>();
  for (const m of meses) {
    const k = m.comp_key;
    if (!map.has(k))
      map.set(k, {
        key: k, label: m.comp_label,
        bruto: 0, liquido: 0, inss: 0, irrf: 0, fgts: 0,
        plano: 0, refeicao: 0, taxa: 0, outros: 0, has_mensal: false,
      });
    const e = map.get(k)!;
    const brutoTotal = m.bruto_mensal + m.decimo_terceiro;
    e.bruto += brutoTotal;
    e.liquido += m.liquido_total;
    e.inss += m.inss;
    e.irrf += m.irrf;
    e.fgts += m.fgts;
    e.plano += m.plano;
    e.refeicao += m.refeicao;
    e.taxa += m.taxa;
    e.outros += m.outros;
    if (m.has_mensal) e.has_mensal = true;
  }
  return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
}

function mesAnteriorKey(compKey: string): string {
  const [y, m] = compKey.split("-").map(Number);
  const d = new Date(y!, m! - 1, 1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function agregadoUnico(rows: HoleriteView[], compKey: string): MesAgregado | null {
  const filtrados = rows.filter((r) => r.comp_key === compKey);
  if (!filtrados.length) return null;
  return agregarPorCompetencia(consolidarPorMes(filtrados))[0] ?? null;
}

// ---------------- KPIs ----------------

function calcKpis(rows: HoleriteView[]) {
  const meses = consolidarPorMes(rows);
  const completos = meses.filter((m) => m.has_mensal);
  const sum = (arr: MesConsolidado[], k: keyof MesConsolidado) =>
    arr.reduce((s, m) => s + (m[k] as number), 0);
  return {
    bruto: sum(meses, "bruto_mensal") + sum(meses, "decimo_terceiro"),
    liquido: sum(meses, "liquido_total"),
    inss: sum(meses, "inss"),
    irrf: sum(meses, "irrf"),
    fgts: sum(meses, "fgts"),
    nMeses: completos.length,
    mediaLiq: completos.length ? sum(completos, "liquido_total") / completos.length : 0,
  };
}

// ---------------- Chart defaults ----------------

const CHART_DEFAULTS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: { font: { family: "inherit", size: 12 }, color: "#57534e" },
      position: "bottom" as const,
    },
    tooltip: {
      callbacks: {
        label: (ctx: { dataset: { label?: string }; parsed: { y: number } }) =>
          `${ctx.dataset.label}: ${fmtRS(ctx.parsed.y)}`,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#78716c", font: { family: "inherit", size: 11 } },
    },
    y: {
      grid: { color: "#f5f5f4" },
      ticks: {
        color: "#78716c",
        font: { family: "inherit", size: 11 },
        callback: (v: string | number) =>
          "R$ " + (Number(v) / 1000).toFixed(1) + "k",
      },
    },
  },
} as const;

// ---------------- Render principal ----------------

export function renderDashboard(root: HTMLElement) {
  if (!state.data || !state.session) {
    navigate(state.session ? "start" : "login");
    return;
  }

  // Default mês: 2025 se existir, senão último ano
  const anosDisp = anosDisponiveis(state.data);
  if (!anosDisp.includes(anoAtivo)) {
    anoAtivo = anosDisp.includes("2025") ? "2025" : (anosDisp[anosDisp.length - 1] ?? "todos");
  }

  root.innerHTML = `
    <div class="wrap">
      <header class="app-header">
        <h1>Holerites — Dashboard pessoal</h1>
        <div style="display: flex; gap: 8px; align-items: baseline;">
          <div class="subtitle" id="gerado"></div>
          <button class="btn secondary" id="sair">Sair</button>
        </div>
      </header>

      <div id="alertas-container"></div>

      <div class="filters-block">
        <div class="filters" id="filtros-empresa"></div>
        <div class="filters" id="filtros-periodo"></div>
        <div class="filters" id="filtros-mes"></div>
      </div>

      <section class="kpis" id="kpis"></section>

      <section class="chart-grid" id="vista-historico">
        <div class="card full">
          <h2>Evolução do líquido recebido por mês</h2>
          <div class="chart-wrap"><canvas id="chart-evol"></canvas></div>
        </div>
        <div class="card full">
          <h2>Evolução do salário base</h2>
          <div class="chart-wrap"><canvas id="chart-base"></canvas></div>
        </div>
        <div class="card">
          <h2>Composição de descontos por mês</h2>
          <div class="chart-wrap"><canvas id="chart-desc"></canvas></div>
        </div>
        <div class="card">
          <h2>Bruto vs Líquido recebido</h2>
          <div class="chart-wrap"><canvas id="chart-bl"></canvas></div>
        </div>
      </section>

      <section class="chart-grid" id="vista-mes" hidden>
        <div class="card">
          <h2>Detalhamento de descontos do mês</h2>
          <table id="tabela-descontos">
            <thead><tr><th>Categoria</th><th>Valor</th><th>% bruto</th></tr></thead>
            <tbody></tbody>
          </table>
        </div>
        <div class="card">
          <h2>Comparativo com o mês anterior</h2>
          <div class="chart-wrap"><canvas id="chart-compare"></canvas></div>
          <div class="subtitle" id="compare-subtitle" style="margin-top: 10px; text-align: center;"></div>
        </div>
      </section>

      <section class="card" style="margin-top: 16px;">
        <h2>Detalhamento</h2>
        <div class="table-scroll">
          <table id="tabela">
            <thead><tr>
              <th>Compet.</th><th>Tipo</th><th>Empresa</th>
              <th>Bruto</th><th>Líquido</th><th>INSS</th><th>IRRF</th><th>FGTS</th><th>PDF</th>
            </tr></thead>
            <tbody></tbody>
          </table>
        </div>
      </section>

      <footer>questor.addedlabs.com.br — dados em memória, nada armazenado.</footer>
    </div>
  `;

  const gerado = new Date(state.data.gerado_em);
  document.getElementById("gerado")!.textContent =
    `Atualizado em ${gerado.toLocaleString("pt-BR")}`;
  document.getElementById("sair")!.addEventListener("click", () => {
    clearSession();
    navigate("login");
  });
  attachDownloadHandler();

  render();
}

function anosDisponiveis(data: SyncResponse): string[] {
  const ys = new Set<string>();
  for (const h of data.holerites) {
    const y = h.comp_key.slice(0, 4);
    if (y) ys.add(y);
  }
  return Array.from(ys).sort();
}

function mesesDisponiveisDoAno(data: SyncResponse, ano: string): string[] {
  const ms = new Set<string>();
  for (const h of data.holerites) {
    if (h.comp_key.startsWith(ano)) ms.add(h.comp_key.slice(5, 7));
  }
  return Array.from(ms).sort();
}

function montarFiltros() {
  const data = state.data!;
  const cnpjs = Object.keys(data.empresas);
  const anos = anosDisponiveis(data);

  const grupo = (
    id: string,
    label: string,
    opcoes: Array<[string, string]>,
    getAtivo: () => string,
    setAtivo: (v: string) => void,
  ) => {
    const cont = document.getElementById(id)!;
    cont.innerHTML = `<span class="filter-label">${label}</span>`;
    for (const [lbl, val] of opcoes) {
      const el = document.createElement("button");
      el.className = "chip" + (val === getAtivo() ? " active" : "");
      el.textContent = lbl;
      el.onclick = () => { setAtivo(val); render(); };
      cont.appendChild(el);
    }
  };

  grupo(
    "filtros-empresa", "Empresa",
    [["Todas", "todas"], ...cnpjs.map<[string, string]>((c) => [`${data.empresas[c]} (${c})`, c])],
    () => cnpjAtivo,
    (v) => { cnpjAtivo = v; },
  );
  grupo(
    "filtros-periodo", "Período",
    [["Todos", "todos"], ...anos.map<[string, string]>((a) => [a, a])],
    () => anoAtivo,
    (v) => { anoAtivo = v; mesAtivo = "todos"; },
  );
  const mesCont = document.getElementById("filtros-mes")!;
  if (anoAtivo === "todos") {
    mesCont.innerHTML = "";
    mesCont.style.display = "none";
    return;
  }
  mesCont.style.display = "";
  const meses = mesesDisponiveisDoAno(data, anoAtivo);
  grupo(
    "filtros-mes", "Mês",
    [
      ["Ano inteiro", "todos"],
      ...meses.map<[string, string]>((m) => [MES_LABELS[parseInt(m, 10) - 1]!, m]),
    ],
    () => mesAtivo,
    (v) => { mesAtivo = v; },
  );
}

function renderAlertas() {
  const data = state.data!;
  const cont = document.getElementById("alertas-container")!;
  const alertas = data.alertas.filter((a: AlertaGap) => {
    if (cnpjAtivo !== "todas" && a.empresa_cnpj !== cnpjAtivo) return false;
    if (anoAtivo === "todos") return true;
    if (mesAtivo === "todos") return a.comp_key.startsWith(anoAtivo);
    return a.comp_key === `${anoAtivo}-${mesAtivo}`;
  });
  if (!alertas.length) { cont.innerHTML = ""; return; }
  const itens = alertas
    .map((a) => {
      const faltando = a.faltando?.length
        ? ` — <strong>faltando: ${a.faltando.join(", ")}</strong>`
        : "";
      return `<li><strong>${a.competencia}</strong> em ${a.empresa_nome} — publicado: ${a.tipos_publicados.join(", ")}${faltando}</li>`;
    })
    .join("");
  cont.innerHTML = `
    <div class="alert-banner">
      <div class="alert-icon">⚠</div>
      <div class="alert-body">
        <div class="alert-title">${alertas.length} mês(es) com demonstrativo de folha faltando</div>
        <ul>${itens}</ul>
        <div class="hint">Sugestão: contate o escritório para publicar o(s) demonstrativo(s) faltante(s).</div>
      </div>
    </div>`;
}

function renderKpis(rows: HoleriteView[]) {
  const k = calcKpis(rows);
  document.getElementById("kpis")!.innerHTML = `
    <div class="kpi"><div class="label">Líquido recebido</div>
      <div class="value">${fmtRS(k.liquido)}</div>
      <div class="sub">adi + mensal + 13º consolidados</div></div>
    <div class="kpi"><div class="label">Média mensal</div>
      <div class="value">${fmtRS(k.mediaLiq)}</div>
      <div class="sub">${k.nMeses} mês(es) com Mensal fechado</div></div>
    <div class="kpi"><div class="label">Bruto total</div>
      <div class="value">${fmtRS(k.bruto)}</div>
      <div class="sub">salário + 13º</div></div>
    <div class="kpi"><div class="label">INSS pago</div>
      <div class="value">${fmtRS(k.inss)}</div>
      <div class="sub">${(k.bruto ? (k.inss / k.bruto) * 100 : 0).toFixed(1)}% do bruto</div></div>
    <div class="kpi"><div class="label">IRRF retido</div>
      <div class="value">${fmtRS(k.irrf)}</div>
      <div class="sub">${(k.bruto ? (k.irrf / k.bruto) * 100 : 0).toFixed(1)}% do bruto</div></div>
    <div class="kpi"><div class="label">FGTS depositado</div>
      <div class="value">${fmtRS(k.fgts)}</div>
      <div class="sub">salário + 13º</div></div>
  `;
}

function renderChartsTemporais(rows: HoleriteView[]) {
  const meses = agregarPorCompetencia(consolidarPorMes(rows));
  const labels = meses.map((m) => m.label);

  chartsAtivos.push(
    new Chart(document.getElementById("chart-evol") as HTMLCanvasElement, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Líquido",
            data: meses.map((m) => m.liquido),
            borderColor: "#0e7490",
            backgroundColor: "rgba(14,116,144,.1)",
            borderWidth: 2, tension: 0.25, fill: true, pointRadius: 3,
          },
          {
            label: "Bruto",
            data: meses.map((m) => m.bruto),
            borderColor: "#a8a29e",
            borderWidth: 1, borderDash: [4, 4], pointRadius: 2,
          },
        ],
      },
      options: CHART_DEFAULTS,
    } as ChartConfiguration),
  );

  renderChartSalarioBase(rows);
}

function renderChartSalarioBase(rows: HoleriteView[]) {
  const data = state.data!;
  const mensais = rows.filter((r) => r.tipo_recibo === "Mensal")
    .sort((a, b) => a.comp_key.localeCompare(b.comp_key));
  const todosLabels = Array.from(new Set(mensais.map((r) => r.comp_label)));
  const cnpjs = Array.from(new Set(mensais.map((r) => r.cnpj)));
  const cores = ["#0e7490", "#a16207", "#7c3aed", "#15803d"];

  const datasets = cnpjs.map((cnpj, idx) => {
    const cor = cores[idx % cores.length]!;
    const map = new Map(mensais.filter((r) => r.cnpj === cnpj).map((r) => [r.comp_label, r.salario_base]));
    return {
      label: data.empresas[cnpj] ?? cnpj,
      data: todosLabels.map((l) => map.get(l) ?? null),
      borderColor: cor,
      backgroundColor: cor + "22",
      borderWidth: 2, tension: 0, stepped: "before" as const,
      pointRadius: 4, spanGaps: false,
    };
  });

  const bases = mensais.map((r) => r.salario_base).filter((v) => v > 0);
  const baseMin = bases.length ? Math.min(...bases) : 0;
  const baseMax = bases.length ? Math.max(...bases) : 1;
  const range = Math.max(baseMax - baseMin, baseMax * 0.05);

  chartsAtivos.push(
    new Chart(document.getElementById("chart-base") as HTMLCanvasElement, {
      type: "line",
      data: { labels: todosLabels, datasets },
      options: {
        ...CHART_DEFAULTS,
        scales: {
          ...CHART_DEFAULTS.scales,
          y: {
            ...CHART_DEFAULTS.scales.y,
            suggestedMin: baseMin - range * 0.4,
            suggestedMax: baseMax + range * 0.2,
          },
        },
      },
    } as ChartConfiguration),
  );
}

function renderChartsSempre(rows: HoleriteView[]) {
  const meses = agregarPorCompetencia(consolidarPorMes(rows));
  const labels = meses.map((m) => m.label);

  chartsAtivos.push(
    new Chart(document.getElementById("chart-desc") as HTMLCanvasElement, {
      type: "bar",
      data: {
        labels,
        datasets: [
          { label: "INSS",           data: meses.map((m) => m.inss),     backgroundColor: "#0e7490" },
          { label: "IRRF",           data: meses.map((m) => m.irrf),     backgroundColor: "#dc2626" },
          { label: "Plano de Saúde", data: meses.map((m) => m.plano),    backgroundColor: "#7c3aed" },
          { label: "Refeição",       data: meses.map((m) => m.refeicao), backgroundColor: "#16a34a" },
          { label: "Taxa Assist.",   data: meses.map((m) => m.taxa),     backgroundColor: "#ca8a04" },
          { label: "Outros",         data: meses.map((m) => m.outros),   backgroundColor: "#a8a29e" },
        ],
      },
      options: {
        ...CHART_DEFAULTS,
        scales: {
          ...CHART_DEFAULTS.scales,
          x: { ...CHART_DEFAULTS.scales.x, stacked: true },
          y: { ...CHART_DEFAULTS.scales.y, stacked: true },
        },
      },
    } as ChartConfiguration),
  );

  chartsAtivos.push(
    new Chart(document.getElementById("chart-bl") as HTMLCanvasElement, {
      type: "bar",
      data: {
        labels,
        datasets: [
          { label: "Bruto",   data: meses.map((m) => m.bruto),   backgroundColor: "#1c1917" },
          { label: "Líquido", data: meses.map((m) => m.liquido), backgroundColor: "#0e7490" },
        ],
      },
      options: CHART_DEFAULTS,
    } as ChartConfiguration),
  );
}

function renderVistaMes() {
  const data = state.data!;
  const compAtual = `${anoAtivo}-${mesAtivo}`;
  const baseRows = rowsDoFiltroSemMes(data);
  const atual = agregadoUnico(baseRows, compAtual);
  const prevKey = mesAnteriorKey(compAtual);
  const prev = agregadoUnico(baseRows, prevKey);

  const tbody = document.querySelector("#tabela-descontos tbody")!;
  if (!atual) {
    tbody.innerHTML = `<tr><td colspan="3" style="text-align:center;color:var(--ink-soft);">Sem dados.</td></tr>`;
    document.getElementById("compare-subtitle")!.textContent = "Sem dados para esse mês.";
    return;
  }

  const linhas: Array<{ lbl: string; v: number; sub?: string; bold?: boolean; accent?: boolean }> = [
    { lbl: "Bruto", v: atual.bruto, sub: "salário + 13º" },
    { lbl: "INSS", v: atual.inss },
    { lbl: "IRRF", v: atual.irrf },
    { lbl: "Plano de Saúde", v: atual.plano },
    { lbl: "Refeição", v: atual.refeicao },
    { lbl: "Taxa Assistencial", v: atual.taxa },
    { lbl: "Outros", v: atual.outros },
    {
      lbl: "Total descontos",
      v: atual.inss + atual.irrf + atual.plano + atual.refeicao + atual.taxa + atual.outros,
      bold: true,
    },
    { lbl: "Líquido recebido", v: atual.liquido, bold: true, accent: true },
    { lbl: "FGTS depositado", v: atual.fgts, sub: "pela empresa, não desconto" },
  ];

  tbody.innerHTML = linhas
    .map((l) => {
      const pct = atual.bruto ? ((l.v / atual.bruto) * 100).toFixed(1) + "%" : "—";
      const tr = l.bold ? ' style="font-weight:600;"' : "";
      const td = l.accent ? ' style="color: var(--accent); font-weight:600;"' : "";
      const sub = l.sub ? `<div style="font-size:11px;color:var(--ink-soft);font-weight:400;">${l.sub}</div>` : "";
      return `<tr${tr}><td${td}>${l.lbl}${sub}</td><td${td}>${fmtRS(l.v)}</td><td>${pct}</td></tr>`;
    })
    .join("");

  // Chart comparativo
  const cats: Array<{ k: keyof MesAgregado; lbl: string }> = [
    { k: "bruto", lbl: "Bruto" },
    { k: "liquido", lbl: "Líquido" },
    { k: "inss", lbl: "INSS" },
    { k: "irrf", lbl: "IRRF" },
    { k: "plano", lbl: "Plano Saúde" },
    { k: "refeicao", lbl: "Refeição" },
    { k: "outros", lbl: "Outros" },
  ];
  const prevLabel = `${MES_LABELS[parseInt(prevKey.slice(5, 7), 10) - 1]}/${prevKey.slice(2, 4)}`;
  const atualLabel = `${MES_LABELS[parseInt(mesAtivo, 10) - 1]}/${anoAtivo.slice(2)}`;

  const subtitle = document.getElementById("compare-subtitle")!;
  subtitle.innerHTML = prev
    ? `Variação líquido: <strong>${formatDelta(prev.liquido, atual.liquido)}</strong> vs ${prevLabel}`
    : `Sem dados para ${prevLabel} no escopo selecionado.`;

  const datasets: ChartConfiguration<"bar">["data"]["datasets"] = [];
  if (prev) {
    datasets.push({
      label: prevLabel,
      data: cats.map((c) => prev[c.k] as number),
      backgroundColor: "#a8a29e",
    });
  }
  datasets.push({
    label: atualLabel,
    data: cats.map((c) => atual[c.k] as number),
    backgroundColor: "#0e7490",
  });

  chartsAtivos.push(
    new Chart(document.getElementById("chart-compare") as HTMLCanvasElement, {
      type: "bar",
      data: { labels: cats.map((c) => c.lbl), datasets },
      options: CHART_DEFAULTS,
    } as ChartConfiguration),
  );
}

function formatDelta(antes: number, depois: number): string {
  if (!antes) return "+" + fmtRS(depois);
  const pct = ((depois - antes) / antes) * 100;
  const sinal = pct >= 0 ? "+" : "";
  return `${sinal}${pct.toFixed(1)}%  (${sinal}${fmtRS(depois - antes)})`;
}

/** Documento (com DownloadId) correspondente a um holerite, ou undefined. */
function docComDownload(docId: string): DocComp | undefined {
  if (!docId) return undefined;
  const d = state.data?.documentos.find((x) => x.id === docId);
  return d && d.download_id ? d : undefined;
}

/** Nome amigável para o arquivo salvo localmente. */
function nomeArquivo(doc: DocComp): string {
  const partes = [doc.empresa_nome, doc.competence, doc.category]
    .filter(Boolean)
    .join(" - ");
  const limpo = partes.replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, " ").trim();
  return (limpo || "documento") + ".pdf";
}

/**
 * Listener delegado (anexado uma vez no mount) para os botões de download da
 * tabela. Baixa o PDF via Worker e dispara o "salvar" do browser — nada fica
 * armazenado: o object URL é revogado logo após o clique.
 */
function attachDownloadHandler() {
  const tabela = document.getElementById("tabela");
  if (!tabela) return;
  tabela.addEventListener("click", async (ev) => {
    const alvo = ev.target as HTMLElement;
    const btn = alvo.closest<HTMLButtonElement>("button[data-doc]");
    if (!btn) return;
    const doc = docComDownload(btn.dataset.doc ?? "");
    if (!doc || !state.session) return;

    const rotulo = btn.textContent;
    btn.disabled = true;
    btn.textContent = "…";
    try {
      const blob = await apiDownload(state.session, {
        download_id: doc.download_id,
        doc_id: doc.id,
        download_filename: doc.download_filename,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nomeArquivo(doc);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      btn.textContent = rotulo;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        clearSession();
        navigate("login");
        return;
      }
      btn.textContent = "✗ erro";
      window.setTimeout(() => {
        btn.textContent = rotulo;
      }, 2500);
    } finally {
      btn.disabled = false;
    }
  });
}

function renderTabela(rows: Array<HoleriteView & { _reemitido: boolean }>) {
  const tbody = document.querySelector("#tabela tbody")!;
  const ord = rows
    .slice()
    .sort((a, b) => b.comp_key.localeCompare(a.comp_key) || a.tipo_recibo.localeCompare(b.tipo_recibo));
  tbody.innerHTML = ord
    .map((r) => {
      const tagCls = r.tipo_recibo === "Mensal"
        ? "mensal"
        : r.tipo_recibo.startsWith("Adiant")
        ? "adi"
        : r.tipo_recibo.startsWith("13")
        ? "dec"
        : "";
      const reem = r._reemitido
        ? '<span class="tag" style="background:#fef3c7;color:#92400e;margin-left:6px;" title="Versão anterior reemitida pelo escritório">reemitido</span>'
        : "";
      const rowStyle = r._reemitido ? ' style="opacity:.55;"' : "";
      return `<tr${rowStyle}>
        <td>${r.competencia || "—"}</td>
        <td><span class="tag ${tagCls}">${r.tipo_recibo}</span>${reem}</td>
        <td>${r.empresa_nome}<span class="empresa-cell">${r.cnpj}</span></td>
        <td>${fmtBR(r.total_proventos)}</td>
        <td><strong>${fmtBR(r.liquido)}</strong></td>
        <td>${fmtBR(r.inss_valor)}</td>
        <td>${fmtBR(r.irrf_valor)}</td>
        <td>${fmtBR(r.fgts_mes)}</td>
        <td>${
          docComDownload(r.doc_id)
            ? `<button class="btn-pdf" type="button" data-doc="${r.doc_id}" title="Baixar PDF original">⬇ PDF</button>`
            : "—"
        }</td>
      </tr>`;
    })
    .join("");
}

function render() {
  if (!state.data) return;
  montarFiltros();
  renderAlertas();
  clearCharts();

  const rows = rowsFiltrados(state.data);
  renderKpis(rows);

  const mesEspecifico = anoAtivo !== "todos" && mesAtivo !== "todos";
  document.getElementById("vista-historico")!.hidden = mesEspecifico;
  document.getElementById("vista-mes")!.hidden = !mesEspecifico;

  if (mesEspecifico) {
    renderVistaMes();
  } else {
    renderChartsTemporais(rows);
    renderChartsSempre(rows);
  }
  renderTabela(rowsReemissoes(state.data));
}
